import { useCallback, useEffect, useRef, useState } from "react";
import type { CareTeamPortraitState } from "@care-by-ella/contracts";
import { mediaPath } from "@care-by-ella/contracts";
import {
  fetchMyPortrait,
  uploadMyPortrait,
  UnauthorizedError,
} from "@/lib/clinician-api";
import { useAuth } from "@/auth/AuthContext";
import { Avatar } from "@/components/Avatar";

// TES-497 (Ö-36): the doctor and the coach set their OWN
// portrait — the one the app shows next to "Din läkare" / "Din coach" in the
// chat list, the chat header and the care-team cards (TES-485). This is their
// surface, not the content library: no key, no alt text, one button. The API
// decides the slot from the session (TES-719: the signed-in employee's own
// key, not the role's), so the page only ever moves bytes.

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

// The app renders the portrait at ≤ 48 pt, so 512 px square is generous —
// and it keeps a 12 MP phone photo far under the platform's 2 MB cap
// (MEDIA_MAX_BYTES) without the clinician having to resize anything.
const OUTPUT_PX = 512;
const ACCEPTED = ["image/jpeg", "image/png", "image/webp"];

// Where the app shows it — the preview mirrors those sizes exactly.
const APP_SIZES: Array<{ px: number; where: string }> = [
  { px: 48, where: "Chattlistan" },
  { px: 44, where: "Chatten" },
  { px: 32, where: "Vårdteamskort" },
];

// Centre-crop to a square and downscale on a canvas. Output is always JPEG:
// portraits have no transparency to preserve and JPEG is the smallest of the
// three accepted formats.
async function normalisePortrait(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  try {
    const side = Math.min(bitmap.width, bitmap.height);
    const sx = (bitmap.width - side) / 2;
    const sy = (bitmap.height - side) / 2;
    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT_PX;
    canvas.height = OUTPUT_PX;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Kunde inte bearbeta bilden i webbläsaren.");
    ctx.drawImage(bitmap, sx, sy, side, side, 0, 0, OUTPUT_PX, OUTPUT_PX);
    return canvas.toDataURL("image/jpeg", 0.85);
  } finally {
    bitmap.close();
  }
}

export function MyProfileRoute() {
  const { signOut, claims } = useAuth();
  const [state, setState] = useState<CareTeamPortraitState | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<string | null>(null); // data URL
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const roleLabel = claims?.role === "coach" ? "Din coach" : "Din läkare";

  const load = useCallback(async () => {
    try {
      setState(await fetchMyPortrait());
      setLoadError(null);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setLoadError(e instanceof Error ? e.message : String(e));
    }
  }, [signOut]);

  useEffect(() => {
    void load();
  }, [load]);

  async function onPick() {
    const file = fileRef.current?.files?.[0];
    setSaved(false);
    setError(null);
    if (!file) {
      setPending(null);
      return;
    }
    if (!ACCEPTED.includes(file.type)) {
      setPending(null);
      setError("Välj en JPEG-, PNG- eller WebP-bild.");
      return;
    }
    try {
      setPending(await normalisePortrait(file));
    } catch (e) {
      setPending(null);
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function onSave() {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      await uploadMyPortrait({
        mimeType: "image/jpeg",
        dataBase64: pending.slice(pending.indexOf(",") + 1),
      });
      setPending(null);
      if (fileRef.current) fileRef.current.value = "";
      await load();
      setSaved(true);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // TES-719 — until her own upload, patients see the tenant's shared role
  // portrait, so that is what the preview shows.
  const shown = state?.asset ?? state?.fallback ?? null;
  const currentSrc = shown
    ? `${API_BASE}${mediaPath(shown.key, shown.version)}`
    : undefined;
  const showingShared = !state?.asset && !!state?.fallback && !pending;
  // The pending crop takes the preview over so the clinician sees the result
  // in the app's sizes before committing to it. Keyed below: Avatar's
  // load-failure fallback is sticky per instance, so a new src must remount it.
  const previewSrc = pending ?? currentSrc;

  return (
    <div className="flex flex-col gap-md">
      <h1 className="font-display text-3xl tracking-tight">Min profil</h1>

      <section className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-md max-w-2xl">
        <div className="flex flex-col gap-xs">
          <h2 className="text-lg font-semibold">Porträtt</h2>
          <p className="text-sm text-textSecondary">
            Bilden visas för patienterna bredvid ”{roleLabel}” i chatten och på
            vårdteamskorten. Den beskärs automatiskt till en cirkel — välj gärna
            en bild där ansiktet är i mitten.
          </p>
        </div>

        {loadError ? (
          <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
            Kunde inte hämta ditt porträtt: {loadError}
          </div>
        ) : state === null ? (
          <p className="text-sm text-textSecondary">Hämtar…</p>
        ) : (
          <div className="flex flex-wrap items-end gap-lg">
            {APP_SIZES.map(({ px, where }) => (
              <figure key={px} className="flex flex-col items-center gap-xs">
                <Avatar
                  key={previewSrc ?? "none"}
                  name={roleLabel}
                  src={previewSrc}
                  size={px}
                />
                <figcaption className="text-xs text-textSecondary">
                  {where}
                </figcaption>
              </figure>
            ))}
            {!previewSrc ? (
              <p className="text-sm text-textSecondary self-center">
                Inget porträtt ännu — patienterna ser en siluett tills du laddar
                upp en bild.
              </p>
            ) : showingShared ? (
              <p className="text-sm text-textSecondary self-center">
                Du har inget eget porträtt ännu — patienterna ser vårdteamets
                gemensamma bild tills du laddar upp en egen.
              </p>
            ) : null}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-md">
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPTED.join(",")}
            className="text-sm"
            disabled={busy || state === null}
            onChange={() => void onPick()}
          />
          <button
            type="button"
            disabled={busy || !pending}
            onClick={() => void onSave()}
            className="bg-brand text-white rounded-md px-md py-xs text-sm disabled:opacity-50"
          >
            {busy
              ? "Sparar…"
              : state?.asset
                ? "Byt porträtt"
                : "Spara porträtt"}
          </button>
          {saved ? (
            <span className="text-sm text-textSecondary" role="status">
              Sparat. Appen visar den nya bilden inom en timme.
            </span>
          ) : null}
        </div>

        {error ? (
          <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
            {error}
          </div>
        ) : null}
      </section>
    </div>
  );
}
